import { useCallback, useEffect, useRef, useState } from 'react';

export const UPLOADED = 'UPLOADED';
export const UPLOADING = 'UPLOADING';

const FAILED_UPLOAD_STATUSES: ReadonlyArray<string> = ['FAILED', 'TXT_EXTRACT_FAILED'];

// stillProcessing: still uploading after a manual check, or when the page opened mid-upload.
// checkFailed: the status request itself failed, so the outcome is unknown.
export type UploadPhase = 'idle' | 'processing' | 'slow' | 'stillProcessing' | 'uploaded' | 'failed' | 'checkFailed';

export interface UploadCheckResponse {
    success: boolean;
    aborted?: boolean;
    data?: unknown;
}

interface Run {
    check: () => Promise<UploadCheckResponse>;
    // Receives the poll response body, which carries the uploaded file's details
    onUploaded?: (data: unknown) => void;
}

interface Options {
    fastIntervalMs?: number;
    fastWindowMs?: number;
    slowIntervalMs?: number;
}

export const isUploadInProgress = (phase: UploadPhase) =>
    phase === 'processing' || phase === 'slow' || phase === 'stillProcessing' || phase === 'checkFailed';

// `status` is still read so documents saved before the backend added `uploadStatus` keep working
export const readUploadStatus = (data: unknown): string | undefined => {
    if (!data || typeof data !== 'object') {
        return undefined;
    }
    const { uploadStatus, status } = data as { uploadStatus?: unknown; status?: unknown };
    const value = uploadStatus ?? status;
    return typeof value === 'string' && value !== '' ? value.toUpperCase() : undefined;
};

export const isFailedUploadStatus = (status: string | undefined): boolean =>
    Boolean(status && FAILED_UPLOAD_STATUSES.includes(status));

/**
 * Polls every 3 seconds for the first minute, then every 30 seconds while the page is open. Stops on
 * UPLOADED, on any other status (a processing failure), or when the status request fails.
 *
 * @param options polling intervals, overridable for tests
 * @returns the phase, the last check time, and the functions to control polling
 */
export const useUploadStatus = ({
    fastIntervalMs = 3_000,
    fastWindowMs = 60_000,
    slowIntervalMs = 30_000,
}: Options = {}) => {
    const [phase, setPhase] = useState<UploadPhase>('idle');
    const [lastCheckedAt, setLastCheckedAt] = useState<Date>();

    const runRef = useRef<Run | null>(null);
    const runIdRef = useRef(0);
    const startedAtRef = useRef(0);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inFlightRef = useRef(false);
    const missedWhileHiddenRef = useRef(false);
    const mountedRef = useRef(true);
    // The polling timer runs outside a render, so it reads the phase from here
    const phaseRef = useRef<UploadPhase>('idle');

    const changePhase = useCallback((next: UploadPhase) => {
        phaseRef.current = next;
        setPhase(next);
    }, []);

    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const runCheck = useCallback(
        async (source: 'poll' | 'manual') => {
            const run = runRef.current;
            if (!run || inFlightRef.current) {
                return;
            }
            const runId = runIdRef.current;
            inFlightRef.current = true;

            let response: UploadCheckResponse;
            try {
                response = await run.check();
            } catch {
                response = { success: false };
            }
            inFlightRef.current = false;

            // The page closed, or a new upload replaced this one while the request was out
            if (!mountedRef.current || runId !== runIdRef.current || response.aborted) {
                return;
            }

            setLastCheckedAt(new Date());
            const status = response.success ? readUploadStatus(response.data) : undefined;

            if (status === UPLOADED) {
                clearTimer();
                changePhase('uploaded');
                run.onUploaded?.(response.data);
                return;
            }

            if (status === UPLOADING) {
                const isFastWindow = Date.now() - startedAtRef.current < fastWindowMs;
                const nextPhase: UploadPhase =
                    source === 'manual' || phaseRef.current === 'stillProcessing'
                        ? 'stillProcessing'
                        : isFastWindow
                          ? 'processing'
                          : 'slow';
                changePhase(nextPhase);
                clearTimer();
                timerRef.current = setTimeout(
                    () => {
                        timerRef.current = null;
                        // Checking a background tab only adds load; catch up when it is visible
                        if (document.hidden) {
                            missedWhileHiddenRef.current = true;
                            return;
                        }
                        void runCheck('poll');
                    },
                    isFastWindow ? fastIntervalMs : slowIntervalMs
                );
                return;
            }

            clearTimer();
            changePhase(status ? 'failed' : 'checkFailed');
        },
        [changePhase, clearTimer, fastIntervalMs, fastWindowMs, slowIntervalMs]
    );

    const begin = useCallback(
        (run: Run, nextPhase: UploadPhase, startedAt: number) => {
            runIdRef.current += 1;
            clearTimer();
            inFlightRef.current = false;
            missedWhileHiddenRef.current = false;
            runRef.current = run;
            startedAtRef.current = startedAt;
            changePhase(nextPhase);
            void runCheck('poll');
        },
        [changePhase, clearTimer, runCheck]
    );

    const start = useCallback((run: Run) => begin(run, 'processing', Date.now()), [begin]);

    // An upload already running when the page opened is past its first minute, so poll slowly
    const resume = useCallback(
        (run: Run) => begin(run, 'stillProcessing', Date.now() - fastWindowMs),
        [begin, fastWindowMs]
    );

    const checkAgain = useCallback(() => {
        clearTimer();
        missedWhileHiddenRef.current = false;
        void runCheck('manual');
    }, [clearTimer, runCheck]);

    const reset = useCallback(() => {
        runIdRef.current += 1;
        clearTimer();
        runRef.current = null;
        changePhase('idle');
    }, [changePhase, clearTimer]);

    const markFailed = useCallback(() => {
        runIdRef.current += 1;
        clearTimer();
        runRef.current = null;
        changePhase('failed');
    }, [changePhase, clearTimer]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden && missedWhileHiddenRef.current) {
                missedWhileHiddenRef.current = false;
                void runCheck('poll');
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [runCheck]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            runIdRef.current += 1;
            clearTimer();
        };
    }, [clearTimer]);

    return { phase, lastCheckedAt, start, resume, checkAgain, reset, markFailed };
};
